import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
    { to: "/design", label: "Design" },
    { to: "/consult", label: "Consult" },
    { to: "/learn", label: "Learn" },
    { to: "/work", label: "Work" },
    { to: "/about", label: "About" },
    { to: "/blog", label: "Journal" },
];

const Navigation = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    // Pages that DO NOT have a dark hero at the top → nav must always show dark text
    const isDarkHeroPage = location.pathname === "/";
    // Light nav = cream text with shadow (over dark hero). Dark nav = ink text on cream chrome.
    const useLightNav = isDarkHeroPage && !scrolled;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const logoText = useLightNav ? "text-[var(--st-cream)]" : "text-[var(--st-ink)]";
    const logoSub = useLightNav ? "text-[var(--st-gold)]" : "text-[var(--st-gold-deep)]";
    const linkBase = useLightNav ? "text-[var(--st-cream)]" : "text-[var(--st-ink)]";
    const linkHover = "hover:text-[var(--st-gold)]";
    const toggleColor = useLightNav ? "text-[var(--st-cream)]" : "text-[var(--st-ink)]";
    // Text-shadow only when over hero, to keep logo/links readable against varied bright imagery
    const lightShadow = useLightNav ? { textShadow: "0 1px 12px rgba(0,0,0,0.55), 0 0 3px rgba(0,0,0,0.35)" } : {};

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled ? "backdrop-blur-md bg-[rgba(235,233,222,0.88)] border-b border-[var(--st-line)]" : "bg-transparent"
            }`}
        >
            <div className="st-container flex items-center justify-between py-5">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group" style={lightShadow}>
                    <span className={`font-serif text-2xl tracking-tight leading-none ${logoText}`}>SilverTree</span>
                    <span className={`eyebrow text-[10px] mt-2 hidden sm:inline ${logoSub}`}>Design Studio</span>
                </Link>

                <nav className="hidden lg:flex items-center gap-9" style={lightShadow}>
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-[13px] tracking-wider uppercase transition-colors ${linkHover} ${
                                    isActive ? "text-[var(--st-gold)]" : linkBase
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <Link to="/contact" data-testid="nav-cta-lets-talk" className="hidden sm:inline-flex btn-primary">
                        Let's talk <ArrowUpRight size={16} />
                    </Link>
                    <button
                        data-testid="nav-mobile-toggle"
                        aria-label="Toggle navigation"
                        className={`lg:hidden p-2 ${toggleColor}`}
                        onClick={() => setOpen((v) => !v)}
                        style={lightShadow}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden bg-[var(--st-cream)] border-t border-[var(--st-line)]">
                    <div className="st-container py-6 flex flex-col gap-5">
                        {links.map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                                className="font-serif text-3xl text-[var(--st-ink)]"
                            >
                                {l.label}
                            </Link>
                        ))}
                        <Link to="/contact" data-testid="mobile-nav-cta" className="btn-primary self-start mt-3">
                            Let's talk <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navigation;
